import { format } from "date-fns";
import { toZonedTime } from "date-fns-tz";
import {
  ActivityLogManyQuery,
  EnumActivityLogType,
  EnumNoteKind,
  SortFindManyActivityLogInput,
  useActivityLogManyQuery,
  useMembershipTimelineManyQuery,
  NoteManyQuery,
} from "gql/generated";
import _ from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNotesContext } from "shared/contextProviders";
import { DATE_PATTERN_SIMPLE } from "shared/utils";

export type RenewalEmailActivityLog =
  ActivityLogManyQuery["activityLogMany"][number];

export enum OperatorTimelineFilter {
  NOTES = "NOTES",
  SYSTEM = "SYSTEM",
  STAFF = "STAFF",
}

export type OperatorTimelineItem = {
  _id: string;
  groupByDate: string;
  filterGroup: OperatorTimelineFilter;
  note?: NoteManyQuery["noteMany"][number];
  systemLabel?: string;
  systemKind?: string;
  systemViaSuffix?: string;
  systemActorName?: string | null;
  staffPersonName?: string;
  /** Present for clickable renewal-email rows; opens the email tracking slideout. */
  renewalEmailLog?: RenewalEmailActivityLog;
};

export type OperatorTimelineGroup = {
  groupByDate: string;
  items: OperatorTimelineItem[];
};

const MEMBERSHIP_NOTE_KINDS = new Set<string>([
  EnumNoteKind.MembershipStatusAdded,
  EnumNoteKind.MembershipStatusRemoved,
  EnumNoteKind.MembershipTierAdded,
  EnumNoteKind.MembershipTierRemoved,
]);

function noteGroupDate(note: NoteManyQuery["noteMany"][number]): string {
  return note.noteDateISO
    ? format(toZonedTime(note.noteDateISO, "UTC"), DATE_PATTERN_SIMPLE)
    : format(note.createdDateISO ?? "", DATE_PATTERN_SIMPLE);
}

function objectIdToDate(objectId: string): Date | null {
  if (!objectId || objectId.length < 8) return null;
  const timestamp = parseInt(objectId.substring(0, 8), 16);
  if (Number.isNaN(timestamp)) return null;
  return new Date(timestamp * 1000);
}

export type UseOperatorNotesTimelineProps = {
  operatorEntityId: string;
  organizationId?: string;
  staffNotes?: NoteManyQuery["noteMany"];
  staffNotesLoading?: boolean;
};

export const useOperatorNotesTimeline = ({
  operatorEntityId,
  organizationId,
  staffNotes,
  staffNotesLoading,
}: UseOperatorNotesTimelineProps) => {
  const { notes, loading: notesLoading, error: notesError } = useNotesContext();

  const {
    data: membershipData,
    loading: membershipLoading,
  } = useMembershipTimelineManyQuery({
    variables: {
      subjectType: "OPERATOR",
      subjectEntityId: operatorEntityId,
      organizationId: organizationId ?? "",
    },
    skip: !operatorEntityId || !organizationId,
    fetchPolicy: "cache-and-network",
  });

  const { data: renewalEmailData, loading: renewalEmailLoading } =
    useActivityLogManyQuery({
      variables: {
        filter: {
          entityIds: [operatorEntityId],
          types: [EnumActivityLogType.EmailRenewalSent],
        },
        sort: SortFindManyActivityLogInput.CreatedDesc,
        limit: 100,
      },
      skip: !operatorEntityId,
      fetchPolicy: "cache-and-network",
    });

  const [selectedFilter, setSelectedFilter] = useState<OperatorTimelineFilter | null>(null);

  const allItems = useMemo(() => {
    const items: OperatorTimelineItem[] = [];

    // Build a set of MembershipEvent-sourced IDs to avoid duplicate display
    const membershipEventIds = new Set(
      (membershipData?.membershipTimelineMany ?? []).map(i => i._id),
    );

    for (const note of notes ?? []) {
      if (note.isArchived) continue;
      if (MEMBERSHIP_NOTE_KINDS.has(note.kind)) {
        // Show old membership notes as SYSTEM items (fallback when MembershipEvent records don't exist)
        if (membershipEventIds.size === 0) {
          items.push({
            _id: note._id,
            groupByDate: noteGroupDate(note),
            filterGroup: OperatorTimelineFilter.SYSTEM,
            systemLabel: note.message,
          });
        }
        continue;
      }
      items.push({
        _id: note._id,
        groupByDate: noteGroupDate(note),
        filterGroup: OperatorTimelineFilter.NOTES,
        note,
      });
    }

    // Membership events from MembershipEvent records
    for (const item of membershipData?.membershipTimelineMany ?? []) {
      items.push({
        _id: item._id,
        groupByDate: format(
          toZonedTime(item.createdDateISO, "UTC"),
          DATE_PATTERN_SIMPLE,
        ),
        filterGroup: OperatorTimelineFilter.SYSTEM,
        systemLabel: item.tagName,
        systemKind: item.kind,
        systemViaSuffix: item.viaSuffix,
        systemActorName: item.actorName,
      });
    }

    // Renewal-engine emails (clickable → email tracking slideout)
    for (const log of renewalEmailData?.activityLogMany ?? []) {
      if (!log) continue;
      const createdISO = log.createdDateISO;
      items.push({
        _id: `renewal_email_${log._id}`,
        groupByDate: createdISO
          ? format(toZonedTime(createdISO, "UTC"), DATE_PATTERN_SIMPLE)
          : format(new Date(), DATE_PATTERN_SIMPLE),
        filterGroup: OperatorTimelineFilter.SYSTEM,
        systemKind: "RENEWAL_EMAIL_SENT",
        systemLabel: log.emailActivity?.subject ?? "",
        renewalEmailLog: log,
      });
    }

    // Operator created pseudo-event
    const entityCreatedDate = objectIdToDate(operatorEntityId);
    if (entityCreatedDate) {
      items.push({
        _id: "__operator_created__",
        groupByDate: format(entityCreatedDate, DATE_PATTERN_SIMPLE),
        filterGroup: OperatorTimelineFilter.SYSTEM,
        systemLabel: "",
        systemKind: "OPERATOR_CREATED",
      });
    }

    // Staff notes
    for (const note of staffNotes ?? []) {
      const authorName = note.author
        ? `${note.author.firstName ?? ""} ${note.author.lastName ?? ""}`.trim()
        : "";
      items.push({
        _id: `staff_${note._id}`,
        groupByDate: noteGroupDate(note),
        filterGroup: OperatorTimelineFilter.STAFF,
        note,
        staffPersonName: authorName,
      });
    }

    return items;
  }, [
    notes,
    operatorEntityId,
    staffNotes,
    membershipData?.membershipTimelineMany,
    renewalEmailData?.activityLogMany,
  ]);

  const [timelineGroups, setTimelineGroups] = useState<OperatorTimelineGroup[]>([]);
  const [filteredGroups, setFilteredGroups] = useState<OperatorTimelineGroup[]>([]);

  useEffect(() => {
    const byDate = _.groupBy(allItems, "groupByDate");
    const groups = Object.entries(byDate).map(([groupByDate, items]) => ({
      groupByDate,
      items,
    }));
    const sorted = _.orderBy(groups, "groupByDate", "desc");
    setTimelineGroups(sorted);
    setFilteredGroups(
      selectedFilter
        ? sorted
            .map(g => ({
              ...g,
              items: g.items.filter(i => i.filterGroup === selectedFilter),
            }))
            .filter(g => g.items.length > 0)
        : sorted,
    );
  }, [allItems, selectedFilter]);

  const handleFilterClick = useCallback(
    (filter: OperatorTimelineFilter) => {
      const next = selectedFilter === filter ? null : filter;
      setSelectedFilter(next);
    },
    [selectedFilter],
  );

  const filterOptions = useMemo(
    () =>
      Object.values(OperatorTimelineFilter).map(f => ({
        label: f,
        value: f,
      })),
    [],
  );

  const [isAddingNote, setIsAddingNote] = useState(false);
  const handleNoteSaveSuccess = () => setIsAddingNote(false);

  return {
    filterOptions,
    selectedFilter,
    timelineGroups,
    filteredGroups,
    loading:
      notesLoading ||
      !!staffNotesLoading ||
      membershipLoading ||
      renewalEmailLoading,
    notesError,
    handleFilterClick,
    isAddingNote,
    setIsAddingNote,
    handleNoteSaveSuccess,
  };
};
